import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, SortAsc, SortDesc, Eye, Edit, Star, StarOff, 
  Grid, List, MoreVertical, Users, BookOpen, TrendingUp, 
  CheckCircle, AlertCircle, Clock, RefreshCw, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import '../styles/CollectionAdmin.css';

const CollectionAdmin = () => {
  // State management
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  // Filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('collection');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // View mode
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedCollections, setSelectedCollections] = useState([]);
  
  // Load collections using the RPC function
  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (currentPage - 1) * pageSize;
      
      // Filter to only show collections with type 'collection', 'show', or 'event'
      const { data, error: rpcError } = await supabase.rpc('get_admin_collection_review', {
        curator_uuid_text: null, // For now, show all collections
        collection_type_filter: typeFilter, // Use the selected type filter
        featured_status: featuredFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit_val: pageSize,
        offset_val: offset,
        search_query: searchQuery,
        metadata_only: true // Get both metadata and collections
      });
      
      if (rpcError) throw rpcError;
      
      if (data) {
        setCollections(data.collections || []);
        setStats(data.collection_statistics);
        setTotalCount(data.metadata?.total_count || 0);
      }
      
    } catch (err) {
      console.error('Error loading collections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, typeFilter, featuredFilter, sortBy, sortOrder, searchQuery]);
  
  // Load collections when dependencies change
  useEffect(() => {
    loadCollections();
  }, [loadCollections]);
  
  // Feature/unfeature collection
  const toggleFeatureStatus = async (collectionId, currentlyFeatured) => {
    try {
      if (currentlyFeatured) {
        // Remove from spotlights
        const { error } = await supabase
          .from('admin_curated_spotlights')
          .delete()
          .eq('spotlight_type', 'collection')
          .eq('spotlight_id', collectionId);
        
        if (error) throw error;
      } else {
        // Add to spotlights
        const { error } = await supabase
          .from('admin_curated_spotlights')
          .insert({
            spotlight_type: 'collection',
            spotlight_id: collectionId,
            label: 'Featured Collection',
            priority: 1
          });
        
        if (error) throw error;
      }
      
      // Reload collections
      loadCollections();
      
    } catch (err) {
      console.error('Error toggling feature status:', err);
      setError(err.message);
    }
  };
  
  // Export collections data
  const exportCollections = async () => {
    try {
      // Get all collections without pagination for export
      const { data, error } = await supabase.rpc('get_admin_collection_review', {
        curator_uuid_text: null,
        collection_type_filter: typeFilter,
        featured_status: featuredFilter,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit_val: 1000, // Large limit for export
        offset_val: 0,
        search_query: searchQuery,
        metadata_only: false
      });
      
      if (error) throw error;
      
      // Convert to CSV
      const csvData = data.collections.map(collection => ({
        'Collection ID': collection.collection_id,
        'Title': collection.title,
        'Type': collection.type,
        'Creator': collection.creator.name,
        'Artwork Count': collection.artwork_counts.public,
        'Views': collection.engagement_metrics.total_views,
        'Bookmarks': collection.engagement_metrics.total_bookmarks,
        'Featured': collection.is_currently_featured ? 'Yes' : 'No',
        'Quality': collection.quality_status,
        'Created': new Date(collection.created_at).toLocaleDateString()
      }));
      
      const csvString = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).join(','))
      ].join('\n');
      
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collections_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message);
    }
  };
  
  // Handle search with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Quality status badge component
  const QualityBadge = ({ status }) => {
    const getStatusConfig = (status) => {
      switch (status) {
        case 'complete':
          return { color: 'green', icon: CheckCircle, text: 'Complete' };
        case 'good':
          return { color: 'blue', icon: Clock, text: 'Good' };
        case 'needs_work':
          return { color: 'red', icon: AlertCircle, text: 'Needs Work' };
        default:
          return { color: 'gray', icon: Clock, text: 'Unknown' };
      }
    };
    
    const config = getStatusConfig(status);
    const Icon = config.icon;
    
    return (
      <span className={`quality-badge quality-badge-${config.color}`}>
        <Icon className="quality-badge-icon" color={config.color === 'green' ? '#28a745' : config.color === 'blue' ? '#007bff' : config.color === 'red' ? '#dc3545' : '#6c757d'} />
        {config.text}
      </span>
    );
  };
  
  // Collection card component for grid view
  const CollectionCard = ({ collection }) => (
    <div className="collection-card">
      <div className="collection-thumbnail">
        {collection.cover_image ? (
          <img src={collection.cover_image} alt={collection.title} />
        ) : (
          <div className="collection-placeholder">
            <BookOpen className="placeholder-icon" color="#6c757d" />
          </div>
        )}
      </div>
      
      <div className="collection-info">
        <h3 className="collection-title">{collection.title}</h3>
        <span className="creation-date">
          Created {new Date(collection.created_at).toLocaleDateString()}
        </span>
      </div>
      
      <div className="collection-actions">
        <button 
          onClick={() => navigate(`/admin/collections/${collection.collection_id}`)}
          className="btn-secondary btn-sm"
        >
          <Eye className="btn-icon" color="#6c757d" />
          View
        </button>
      </div>
    </div>
  );
  
  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    
    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} collections
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Previous
          </button>
          <span className="page-indicator">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="collection-admin">
    <div className="collection-admin-container">
      {/* Header */}
      <div className="admin-header">
          <div className="header-content">
            <h1 className="admin-title">Collection Management</h1>
            <p className="admin-subtitle">
              Manage and curate your platform's collection library with comprehensive insights.
            </p>
          </div>
          <div className="header-actions">
            <button onClick={exportCollections} className="btn-secondary">
              <Download className="btn-icon" color="#6c757d" />
              Export
            </button>
            <button onClick={loadCollections} className="btn-primary">
              <RefreshCw className="btn-icon" color="#ffffff" />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div 
              className={`stat-card ${typeFilter === 'all' && featuredFilter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setTypeFilter('all');
                setFeaturedFilter('all');
                setCurrentPage(1);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to show all collections"
            >
              <div className="stat-content">
                <BookOpen className="stat-icon text-blue-500" color="#007bff" />
                <div className="stat-details">
                  <div className="stat-label">Total Collections</div>
                  <div className="stat-value">{stats.total_collections}</div>
                </div>
              </div>
            </div>
            <div 
              className={`stat-card ${featuredFilter === 'featured' ? 'active' : ''}`}
              onClick={() => {
                setFeaturedFilter('featured');
                setTypeFilter('all');
                setCurrentPage(1);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to show featured collections"
            >
              <div className="stat-content">
                <Star className="stat-icon text-yellow-500" color="#ffc107" />
                <div className="stat-details">
                  <div className="stat-label">Featured</div>
                  <div className="stat-value">{stats.featured_count}</div>
                </div>
              </div>
            </div>
            <div 
              className={`stat-card ${sortBy === 'artwork_count' ? 'active' : ''}`}
              onClick={() => {
                setSortBy('artwork_count');
                setSortOrder('desc');
                setTypeFilter('all');
                setFeaturedFilter('all');
                setCurrentPage(1);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to sort by most artworks"
            >
              <div className="stat-content">
                <TrendingUp className="stat-icon text-green-500" color="#28a745" />
                <div className="stat-details">
                  <div className="stat-label">Avg Artworks</div>
                  <div className="stat-value">{stats.average_artwork_count}</div>
                </div>
              </div>
            </div>
            <div 
              className={`stat-card ${typeFilter === 'collection' ? 'active' : ''}`}
              onClick={() => {
                setTypeFilter('collection');
                setFeaturedFilter('all');
                setCurrentPage(1);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to show collections with public art"
            >
              <div className="stat-content">
                <Users className="stat-icon text-purple-500" color="#6f42c1" />
                <div className="stat-details">
                  <div className="stat-label">With Public Art</div>
                  <div className="stat-value">{stats.collections_with_public_art}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Controls */}
        <div className="controls-section">
  <div className="controls-content">
    {/* Search bar - keep it separate and full width */}
    <div className="search-container">
      <Search className="search-icon" color="#6c757d" />
      <input
        type="text"
        placeholder="Search collections, creators, or descriptions..."
        value={searchQuery}
        onChange={handleSearchChange}
        className="search-input"
      />
    </div>
    
    {/* Filters and controls in a separate row */}
    <div className="controls-filters-wrapper">
      <div className="controls-filters">
        <select 
          value={typeFilter} 
          onChange={(e) => setTypeFilter(e.target.value)}
          className="filter-select"
        >
          <option value="collection">Collection</option>
          <option value="show">Show</option>
          <option value="event">Event</option>
        </select>
        
        <select 
          value={featuredFilter} 
          onChange={(e) => setFeaturedFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="featured">Featured</option>
          <option value="not_featured">Not Featured</option>
        </select>
        
        <select 
          value={`${sortBy}-${sortOrder}`} 
          onChange={(e) => {
            const [newSortBy, newSortOrder] = e.target.value.split('-');
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
          }}
          className="filter-select"
        >
          <option value="created_date-desc">Newest First</option>
          <option value="created_date-asc">Oldest First</option>
          <option value="title-asc">Title A-Z</option>
          <option value="title-desc">Title Z-A</option>
          <option value="artwork_count-desc">Most Artworks</option>
          <option value="artwork_count-asc">Least Artworks</option>
          <option value="engagement-desc">Most Engaged</option>
          <option value="creator_name-asc">Creator A-Z</option>
        </select>
      </div>
      
      {/* View toggle on the right */}
      <div className="controls-right">
        <div className="view-toggle">
          <button
            onClick={() => setViewMode('grid')}
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
          >
            <Grid color="#6c757d" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
          >
            <List color="#6c757d" />
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
        
        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <AlertCircle className="error-icon" color="#dc3545" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}
        
        {/* Collections Grid/List */}
        <div className="collections-section">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="loading-spinner" color="#6c757d" />
              <span>Loading collections...</span>
            </div>
          ) : collections.length === 0 ? (
            <div className="empty-state">
              <BookOpen className="empty-icon" color="#6c757d" />
              <h3>No collections found</h3>
              <p>Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <>
              <div className={`collections-container ${viewMode}`}>
                {collections.map((collection) => (
                  <CollectionCard key={collection.collection_id} collection={collection} />
                ))}
              </div>
              
              <Pagination />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionAdmin;